/**
 * Дашборд «Профриски» (`/profrisks`, canon service-prof-risks). Сводные показатели — три окна
 * «Оценено» / «Не оценено» / гистограмма «Уровни рисков» (centrika-docs!33, пришли на смену
 * четырём счётчикам по уровням), таблица должностей (Должность/Работников на должности/Статус
 * оценки/Оценка от, centrika-docs!34) с группировкой по подразделениям, фильтры (подразделение +
 * поиск), кнопка «Оценить» (только на статусе «Не оценено») и блок «Заказы по профрискам».
 * Текст под заголовком (centrika-docs!34) — «Состояние профессиональных рисков в организации» +
 * наименование организации из контекста сессии.
 * Мультивыбор рабочих мест (centrika-docs!35) — механика «Спецоценки» (`useWorkplaceSelection`,
 * свой ключ страницы): чекбокс у каждой строки, включая вакантную, счётчик «Выбрано: N» рядом с
 * «Заказать оценку», заказ с выбором добавляет каждое место отдельной должностью через
 * существующий `positions/batch` шага «Должности». Должность — ссылка на карточку рабочего места, под ней связанные
 * сотрудники «Фамилия И. О.» ссылками на карточки (centrika-docs,
 * page.occupational-safety.profrisks, MR !20) — как в строке «Спецоценки».
 * Строка таблицы = РАБОЧЕЕ МЕСТО (MR !32): два места с одинаковой должностью дают две строки —
 * источник `ListProfriskDashboard` уже отдаёт строку на место, схлопывания по должности нет.
 * У сменного места (`row.shift` + несколько сотрудников) список ФИО предваряется подписью
 * «Сменщики:».
 */
import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  ordersApi,
  profriskApi,
  type OrderCard,
  type ProfriskDashboard,
  type ProfriskDashboardRow,
  type RiskLevel,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { formatShortName } from '@/lib/format-name'
import { backState } from '@/lib/use-back-target'
import { useWorkplaceSelection } from '@/lib/use-workplace-selection'
// Склонение «рабочее место» — тот же счётчик, что у «Спецоценки» (centrika-docs!35 прямо
// отсылает к её механике), поэтому берём готовый хелпер, а не заводим второй такой же.
import { workplacesCountLabel } from '@/lib/sout'
import {
  ASSESSMENT_STATUS_LABELS,
  ASSESSMENT_STATUS_ROLE,
  RISK_LEVEL_VARIANT,
  RISK_LEVEL_VIEW,
  canAssessRisk,
  profriskAssessmentStatus,
} from '@/lib/profrisk'
import { clientBadge, compositionSummary, STAGES_BY_SERVICE } from '@/lib/orders'
import { PAGE_SIZE, pageCount } from '@/lib/pagination'
import { AppLink } from '@/app/navigation'
import { Pagination } from '@/design-system/components/data/pagination'
import { useAuth } from '@/app/auth-context'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Card } from '@/design-system/components/layout/card'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Input } from '@/design-system/components/primitives/input'
import { Checkbox } from '@/design-system/components/primitives/checkbox'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { StatCard } from '@/design-system/components/data/stat-card'
import { Grid } from '@/design-system/components/layout/grid'
import { ProgressBar } from '@/design-system/components/data/progress-bar'
import { ListItem } from '@/design-system/components/data/list-item'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/design-system/components/primitives/tooltip'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableGroupRow,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

const ALL = 'all'
const PROFRISK_STAGE_COUNT = STAGES_BY_SERVICE.profrisk.length

/**
 * Ключ персистентности мультивыбора рабочих мест (centrika-docs!35) — свой у каждой страницы,
 * механика общая (`useWorkplaceSelection`). Маршрут `/profrisks` единственный и без параметров,
 * поэтому общего ключа на вкладку достаточно.
 */
const SELECTION_STORAGE_KEY = 'centrika:profrisk-dashboard:selected-workplaces'

function formatDate(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/**
 * Уровни гистограммы «Уровни рисков» сверху вниз (centrika-docs!33): Высокий → Средний →
 * Допустимый. «Не оценено» в гистограмму не входит — у него своё окно.
 */
const RISK_BARS: Array<Exclude<RiskLevel, 'not_assessed'>> = ['high', 'medium', 'acceptable']

/**
 * Сводные показатели (centrika-docs!33) — три окна слева направо: «Оценено» (сумма должностей с
 * уровнями Высокий/Средний/Допустимый), «Не оценено» и гистограмма «Уровни рисков». У кратких
 * названий окон — подсказка с полным наименованием (`hint` StatCard).
 */
function Summary({ counters }: { counters: ProfriskDashboard['counters'] }) {
  const assessed = counters.high + counters.medium + counters.acceptable
  return (
    // Grid (не StatGrid): окон три, а StatGrid владеет адаптивом набора из ЧЕТЫРЁХ счётчиков
    // (4→2) — ниже контрольной точки третье окно осталось бы половинной ширины, причём именно
    // самое широкое, с гистограммой. Grid сворачивает три колонки в одну.
    <Grid cols={3} className="mb-5 gap-3">
      <StatCard label="Оценено" hint="Должностей с оцененными профессиональными рисками" value={assessed} />
      <StatCard
        label="Не оценено"
        hint="Профессиональные риски не оценены"
        value={counters.notAssessed}
        noData
      />
      <RiskLevelsCard counters={counters} assessed={assessed} />
    </Grid>
  )
}

/**
 * Окно «Уровни рисков» (centrika-docs!33): три горизонтальные полосы сверху вниз, слева название
 * уровня, справа число должностей этого уровня, заполнение — доля уровня среди ОЦЕНЁННЫХ
 * должностей, цвет — цвет уровня из таблицы должностей. Без оценённых должностей полосы пустые,
 * возле каждой — 0. Композиция доменная (специфика профрисков), поэтому живёт на странице;
 * из дизайн-системы взяты поверхность карточки, подсказка и сама полоса.
 */
function RiskLevelsCard({
  counters,
  assessed,
}: {
  counters: ProfriskDashboard['counters']
  assessed: number
}) {
  return (
    <div className="ds-surface rounded-12 px-4 py-3.5">
      <div className="flex items-center gap-1.5 text-13 text-txt-secondary">
        <Tooltip>
          <TooltipTrigger className="ds-focus-ring cursor-help rounded-7 text-left">
            Уровни рисков
          </TooltipTrigger>
          <TooltipContent>Уровни рисков в организации</TooltipContent>
        </Tooltip>
      </div>
      <div className="mt-2 space-y-1.5">
        {RISK_BARS.map((level) => {
          const view = RISK_LEVEL_VIEW[level]
          const count = counters[level]
          // Доля среди оценённых должностей; при их отсутствии полоса не заполнена.
          const percent = assessed > 0 ? (count / assessed) * 100 : 0
          return (
            <div key={level} className="flex items-center gap-2">
              <span className="w-20 shrink-0 text-12 text-txt-secondary">{view.label}</span>
              <ProgressBar
                value={percent}
                variant={RISK_LEVEL_VARIANT[level]}
                label={`${view.label}: ${count}`}
                className="flex-1"
              />
              <span className="w-5 shrink-0 text-right text-13 font-semibold text-txt">{count}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Подпись чекбокса выбора рабочего места (centrika-docs!35). Должность саму по себе брать
 * нельзя: строка таблицы — это рабочее место, и несколько мест с одинаковой должностью — норма
 * (MR !32), так что подписи совпали бы. Добавляем то, чем строки различаются визуально:
 * сотрудников места, а у вакансии — пометку «вакантное».
 */
function selectionLabel(r: ProfriskDashboardRow): string {
  const who =
    r.employees.length === 0
      ? 'вакантное'
      : r.employees.map((e) => formatShortName(e.fullName)).join(', ')
  return `Выбрать рабочее место «${r.position}» — ${who}`
}

function PositionsTable({
  rows,
  onAssess,
  assessing,
  from,
  selected,
  onToggleSelected,
}: {
  rows: ProfriskDashboardRow[]
  onAssess: () => void
  assessing: boolean
  /** Адрес этой страницы (`backState`) — передаётся в ссылки на карточки места и сотрудника
   * как `state.from`, чтобы «Назад» в карточке вернул сюда, а не на фолбэк «Охрана труда». */
  from: string
  /** centrika-docs!35: текущий мультивыбор рабочих мест (набор `workplaceId`) для заказа. */
  selected: Set<string>
  onToggleSelected: (workplaceId: string) => void
}) {
  // Группировка по подразделению с сохранением порядка появления.
  const groups: Array<{ department: string; items: ProfriskDashboardRow[] }> = []
  const idx = new Map<string, number>()
  for (const r of rows) {
    const dep = r.department || 'Без подразделения'
    if (!idx.has(dep)) {
      idx.set(dep, groups.length)
      groups.push({ department: dep, items: [] })
    }
    groups[idx.get(dep)!].items.push(r)
  }

  return (
    <Panel>
      <DataTable>
        <DataTableHead>
          <tr>
            {/* centrika-docs!35: колонка выбора рабочего места — как на «Спецоценке». */}
            <DataTableHeaderCell className="w-10">
              <span className="sr-only">Выбор</span>
            </DataTableHeaderCell>
            <DataTableHeaderCell>Должность</DataTableHeaderCell>
            <DataTableHeaderCell>Работников на должности</DataTableHeaderCell>
            <DataTableHeaderCell>Статус оценки</DataTableHeaderCell>
            <DataTableHeaderCell>Оценка от</DataTableHeaderCell>
            <DataTableHeaderCell>{''}</DataTableHeaderCell>
          </tr>
        </DataTableHead>
        <DataTableBody>
          {groups.map((g) => (
            <GroupRows
              key={g.department}
              group={g}
              onAssess={onAssess}
              assessing={assessing}
              from={from}
              selected={selected}
              onToggleSelected={onToggleSelected}
            />
          ))}
        </DataTableBody>
      </DataTable>
    </Panel>
  )
}

function GroupRows({
  group,
  onAssess,
  assessing,
  from,
  selected,
  onToggleSelected,
}: {
  group: { department: string; items: ProfriskDashboardRow[] }
  onAssess: () => void
  assessing: boolean
  from: string
  selected: Set<string>
  onToggleSelected: (workplaceId: string) => void
}) {
  return (
    <>
      <DataTableGroupRow colSpan={6}>{group.department}</DataTableGroupRow>
      {group.items.map((r, i) => {
        // centrika-docs!34: в строке показывается СТАТУС ОЦЕНКИ («Не оценено» / «Оценено» /
        // «В работе»), а не уровень риска — уровень остался только в гистограмме сводных
        // показателей.
        const status = profriskAssessmentStatus(r)
        return (
          <DataTableRow key={`${r.department}-${r.position}-${i}`}>
            {/* centrika-docs!35: чекбокс есть у КАЖДОЙ строки, включая вакантное рабочее место
                (в заказ профрисков должность попадает независимо от сотрудников — в отличие от
                «Спецоценки», где строки заказа строятся из сотрудников места).
                Подпись несёт не только должность: на этой странице строка = рабочее место, и
                одинаковые должности дают несколько строк (MR !32) — по одному наименованию
                чекбоксы были бы неразличимы на слух. Различитель — то же, что видно в строке:
                сотрудники места либо «вакантное». */}
            <DataTableCell>
              <Checkbox
                checked={selected.has(r.workplaceId)}
                onCheckedChange={() => onToggleSelected(r.workplaceId)}
                aria-label={selectionLabel(r)}
              />
            </DataTableCell>
            <DataTableCell className="font-medium text-txt">
              {/* centrika-docs, page.occupational-safety.profrisks, MR !20: наименование
                  должности — ссылка на карточку рабочего места; `state.from` (адрес этой
                  страницы) нужен, чтобы «Назад» в карточке вернул на пульт профрисков, а не
                  на фолбэк «Охрана труда». */}
              <AppLink to={`/workplaces/${r.workplaceId}`} state={{ from }}>
                {r.position}
              </AppLink>
              {/* Тот же MR: под должностью отдельной строкой — сотрудники места, ФИО в формате
                  «Фамилия И. О.», каждое — ссылка на карточку сотрудника; при отсутствии —
                  «Рабочее место вакантно». Оформление повторяет строку «Спецоценки»
                  (pages/sout-dashboard.tsx).
                  MR !32: если на месте по сменам работают несколько сотрудников, список ФИО
                  предваряется подписью «Сменщики:». Признак сменности — `r.shift` с сервера
                  (есть активный сотрудник с persons.is_shift), а НЕ длина списка: обычный поток
                  создания сотрудника переиспользует место по паре подразделение+должность, так
                  что двое штатных с одинаковой должностью делят место, не работая по сменам.
                  Второе условие (сотрудников больше одного) — буква требования «связан один
                  сотрудник и у него нет сменщиков → только его ФИО»: у сменного места с
                  единственным оставшимся сотрудником подписи нет. */}
              {r.employees.length === 0 ? (
                <p className="mt-0.5 text-12 font-normal text-txt-secondary">Рабочее место вакантно</p>
              ) : (
                <p className="mt-0.5 text-12 font-normal text-txt-secondary">
                  {r.shift && r.employees.length > 1 ? 'Сменщики: ' : null}
                  {r.employees.map((e, idx) => (
                    <span key={e.id}>
                      <AppLink to={`/staff/${e.id}`} state={{ from }}>
                        {formatShortName(e.fullName)}
                      </AppLink>
                      {idx < r.employees.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </p>
              )}
            </DataTableCell>
            <DataTableCell>{r.headcount > 0 ? r.headcount : '—'}</DataTableCell>
            <DataTableCell>
              <Badge status={ASSESSMENT_STATUS_ROLE[status]}>{ASSESSMENT_STATUS_LABELS[status]}</Badge>
            </DataTableCell>
            <DataTableCell>{r.assessedOn ? formatDate(r.assessedOn) : '—'}</DataTableCell>
            <DataTableCell className="text-right">
              {/* centrika-docs, page.occupational-safety.profrisks, MR !23: на странице
                  «Профриски» историческая оценка профрисков не отображается — точка входа
                  осталась только в карточке сотрудника (`company/employee-card.tsx`). */}
              {canAssessRisk(r) ? (
                <Button size="sm" disabled={assessing} onClick={onAssess}>
                  Оценить
                </Button>
              ) : null}
            </DataTableCell>
          </DataTableRow>
        )
      })}
    </>
  )
}

function ProfriskOrders() {
  const navigate = useNavigate()
  const [items, setItems] = useState<OrderCard[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    ordersApi
      .list(
        { service: 'profrisk', limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
        controller.signal,
      )
      .then((res) => {
        if (controller.signal.aborted) return
        // Страница «уехала» за пределы результатов — возвращаемся на первую.
        if (res.items.length === 0 && page > 1) {
          setPage(1)
          return
        }
        setItems(res.items)
        setTotal(res.total)
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [page])

  return (
    <Card header="Заказы по профрискам" className="mt-6">
      {loading ? (
        <p className="py-2 text-txt-secondary">Загрузка…</p>
      ) : items.length === 0 ? (
        <p className="py-2 text-txt-secondary">Заказов по профрискам пока нет.</p>
      ) : (
        items.map((o) => {
          const badge = clientBadge(o.dataStatus)
          const isDraft = o.dataStatus === 'draft'
          return (
            <ListItem
              key={o.id}
              name={o.itemCount > 0 ? compositionSummary(o.service, o.itemCount) : 'Заказ профрисков'}
              meta={`Этап: ${o.currentStage.label} (${o.currentStage.position + 1} из ${PROFRISK_STAGE_COUNT})`}
              action={
                <div className="flex items-center gap-2.5">
                  <Badge status={badge.role}>{badge.label}</Badge>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => navigate(isDraft ? `/orders/${o.id}/profrisk` : `/orders/${o.id}`)}
                  >
                    {isDraft ? 'Продолжить' : 'Открыть'}
                  </Button>
                </div>
              }
            />
          )
        })
      )}
      <Pagination page={page} totalPages={pageCount(total)} onChange={setPage} />
    </Card>
  )
}

export function ProfriskDashboardPage() {
  const navigate = useNavigate()
  const location = useLocation()
  // centrika-docs!34: под заголовком — «Состояние профессиональных рисков в организации» и
  // через пробел наименование организации авторизованного сотрудника. Организация берётся
  // из контекста сессии (`/api/me/context`), доп. запрос не нужен; у аккаунта без
  // организации (страница физлицу недоступна) остаётся одна формулировка без имени.
  const { context } = useAuth()
  const orgName = context?.org?.name ?? ''
  const [data, setData] = useState<ProfriskDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [department, setDepartment] = useState(ALL)
  const [search, setSearch] = useState('')
  const [assessing, setAssessing] = useState(false)
  const [assessError, setAssessError] = useState<string | null>(null)
  // centrika-docs!35: мультивыбор рабочих мест — та же механика, что на «Спецоценке»
  // (общий хук, свой ключ страницы): переживает переход в карточку места или сотрудника и
  // возврат «Назад» в пределах вкладки.
  const {
    selected,
    toggle: toggleSelected,
    clear: clearSelection,
    reconcile: reconcileSelection,
  } = useWorkplaceSelection(SELECTION_STORAGE_KEY)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    profriskApi
      .dashboard(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setData(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить дашборд'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [])

  // centrika-docs!35: «если выбранное рабочее место отсутствует в актуальном списке, его выбор
  // снимается» — сверяем восстановленный выбор со свежими строками дашборда. Выбираемы ВСЕ
  // строки, включая вакантные, поэтому фильтра по строке здесь нет (в отличие от «Спецоценки»).
  useEffect(() => {
    if (!data) return
    reconcileSelection(new Set(data.rows.map((r) => r.workplaceId)))
  }, [data, reconcileSelection])

  const departments = useMemo(() => {
    const set = new Set<string>()
    for (const r of data?.rows ?? []) set.add(r.department || 'Без подразделения')
    return Array.from(set)
  }, [data])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (data?.rows ?? []).filter((r) => {
      const dep = r.department || 'Без подразделения'
      if (department !== ALL && dep !== department) return false
      if (q && !`${r.position} ${dep}`.toLowerCase().includes(q)) return false
      return true
    })
  }, [data, department, search])

  /** Пустой заказ: кнопки «Оценить» в строках и «Заказать оценку» без выбранных мест. */
  async function createOrder() {
    setAssessError(null)
    setAssessing(true)
    try {
      const order = await ordersApi.create({ service: 'profrisk' })
      navigate(`/orders/${order.id}/profrisk`)
    } catch (err) {
      setAssessError(getErrorMessage(err, 'Не удалось создать заказ профрисков'))
    } finally {
      // Снимаем disabled и на успехе (если навигация не размонтировала), и на ошибке —
      // иначе кнопки «Оценить»/«Заказать оценку» залипают заблокированными.
      setAssessing(false)
    }
  }

  /**
   * centrika-docs!35: заказ с выбранными рабочими местами. Каждое место добавляется ОТДЕЛЬНОЙ
   * должностью шага «Должности» существующей механикой добавления из структуры компании
   * (атомарный `positions/batch`, `source: 'structure'`) — совпадающие должности разных мест не
   * схлопываются, поэтому набор строится по строкам дашборда, а не по уникальным должностям.
   *
   * Обработка неудачи (буква требования, та же логика, что у «Спецоценки»): заказ к этому
   * моменту УЖЕ создан, поэтому и отказ добавления, и сетевой сбой не отменяют переход — заказ
   * открывается на первом шаге «Организация», а причина показывается там же предупреждением
   * (`location.state.bulkAddWarning`, читает `OrderProfriskWizardPage`); должности клиент
   * доберёт на шаге «Должности» тем же диалогом «Из структуры компании».
   */
  async function createOrderWithSelection() {
    setAssessError(null)
    setAssessing(true)
    const rows = data?.rows ?? []
    try {
      const order = await ordersApi.create({ service: 'profrisk' })
      // Заказ создан — выбор своё дело сделал, чистим его независимо от исхода добавления.
      const positions = rows
        .filter((r) => selected.has(r.workplaceId))
        .map((r) => ({
          departmentName: r.department,
          position: r.position,
          positionId: r.positionId,
          source: 'structure' as const,
        }))
      clearSelection()

      try {
        await profriskApi.addPositionsBatch(order.id, { positions })
        navigate(`/orders/${order.id}/profrisk`)
      } catch (batchErr) {
        navigate(`/orders/${order.id}/profrisk`, {
          state: {
            bulkAddWarning: getErrorMessage(
              batchErr,
              'Не удалось автоматически добавить выбранные рабочие места в заказ. Добавьте должности на шаге «Должности» через «Из структуры компании».',
            ),
          },
        })
      }
    } catch (err) {
      setAssessError(getErrorMessage(err, 'Не удалось создать заказ профрисков'))
    } finally {
      setAssessing(false)
    }
  }

  function handleHeaderOrderClick() {
    void (selected.size > 0 ? createOrderWithSelection() : createOrder())
  }

  return (
    <div>
      <PageHead
        title="Профриски"
        subtitle={`Состояние профессиональных рисков в организации${orgName ? ` ${orgName}` : ''}`}
        actions={
          <div className="flex items-center gap-3">
            {/* centrika-docs!35: количество выбранных мест — рядом с кнопкой, только при выборе. */}
            {selected.size > 0 ? (
              <span className="text-txt-secondary">Выбрано: {workplacesCountLabel(selected.size)}</span>
            ) : null}
            <Button onClick={handleHeaderOrderClick} disabled={assessing}>
              Заказать оценку
            </Button>
          </div>
        }
      />

      {assessError ? (
        <Alert variant="danger" className="mb-4">
          {assessError}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading || !data ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : (
        <>
          <Summary counters={data.counters} />

          <Toolbar>
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger aria-label="Фильтр по подразделению" className="w-auto">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Все подразделения</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по должности или подразделению"
              className="max-w-narrow"
              aria-label="Поиск должностей"
            />
          </Toolbar>

          {filtered.length === 0 ? (
            <Panel>
              <EmptyState
                icon={AlertTriangle}
                title="Должностей не найдено"
                description="Измените фильтр или добавьте должности в структуре компании"
              />
            </Panel>
          ) : (
            <PositionsTable
              rows={filtered}
              onAssess={() => void createOrder()}
              assessing={assessing}
              from={backState(location).from}
              selected={selected}
              onToggleSelected={toggleSelected}
            />
          )}

          <ProfriskOrders />
        </>
      )}
    </div>
  )
}
